// Sezione "Supporto" per il founder: bottone "Apri un ticket" + lista dei
// propri ticket + thread dialog. Visibile in Impostazioni.
//
// Il dialog "nuovo ticket" e' estratto in SupportNewTicketDialog ed e' anche
// usato dal trigger globale in topbar (SupportTriggerButton). Qui restano
// solo la lista + il dialog del thread esistente.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LifeBuoy, Plus, MessageSquare } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@v2/components/ui/dialog";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { currentIntlLocale } from "../../lib/i18n";
import { SupportNewTicketDialog } from "./SupportNewTicketDialog";

type Status = "open" | "in_progress" | "waiting_user" | "resolved";

type Ticket = {
  id: string;
  user_id: string;
  team_id: string | null;
  title: string;
  category: string;
  status: Status;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
};

type Message = {
  id: string;
  ticket_id: string;
  author_user_id: string | null;
  is_admin: boolean;
  body: string;
  created_at: string;
};

export function SupportSection({ activeTeamId }: { activeTeamId?: string | null }) {
  const { t } = useTranslation("support");
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);

  const ticketsQuery = useQuery({
    queryKey: ["support-tickets-mine"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("support_tickets")
        .select("id, user_id, team_id, title, category, status, created_at, updated_at, resolved_at")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Ticket[];
    },
    staleTime: 30_000,
  });
  const tickets = ticketsQuery.data || [];

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <LifeBuoy className="h-4 w-4 text-primary" />
            {t("card.title")}
          </CardTitle>
          <CardDescription>{t("card.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={() => setNewTicketOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("card.openButton")}
          </Button>

          {ticketsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("list.empty")}</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">{t("list.empty")}</p>
          ) : (
            <ul className="space-y-1.5">
              {tickets.map((tk) => (
                <li key={tk.id}>
                  <button
                    onClick={() => setThreadId(tk.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-2xl bg-background hover:bg-muted/50 text-left transition-colors"
                  >
                    <StatusBadge status={tk.status} />
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium text-sm truncate">{tk.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {new Date(tk.updated_at).toLocaleString(currentIntlLocale(), {
                          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    </span>
                    <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <SupportNewTicketDialog
        open={newTicketOpen}
        onOpenChange={setNewTicketOpen}
        activeTeamId={activeTeamId || null}
        onCreated={() => ticketsQuery.refetch()}
      />

      <ThreadDialog
        ticketId={threadId}
        onClose={() => {
          setThreadId(null);
          ticketsQuery.refetch();
        }}
      />
    </>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const { t } = useTranslation("support");
  const color =
    status === "open" ? "bg-amber-100 text-amber-700 border-amber-200" :
    status === "in_progress" ? "bg-sky-100 text-sky-700 border-sky-200" :
    status === "waiting_user" ? "bg-violet-100 text-violet-700 border-violet-200" :
    "bg-emerald-100 text-emerald-700 border-emerald-200";
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold", color)}>
      {t(`status.${status}`)}
    </span>
  );
}

function ThreadDialog({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const { t } = useTranslation("support");
  const queryClient = useQueryClient();
  const open = !!ticketId;
  const [reply, setReply] = useState("");

  const ticketQuery = useQuery({
    queryKey: ["support-ticket", ticketId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("support_tickets")
        .select("id, user_id, team_id, title, category, status, created_at, updated_at, resolved_at")
        .eq("id", ticketId)
        .maybeSingle();
      if (error) throw error;
      return data as Ticket | null;
    },
    enabled: open,
  });

  const messagesQuery = useQuery({
    queryKey: ["support-messages", ticketId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("support_messages")
        .select("id, ticket_id, author_user_id, is_admin, body, created_at")
        .eq("ticket_id", ticketId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as Message[];
    },
    enabled: open,
    refetchInterval: open ? 15_000 : false,
  });

  const sendReply = useMutation({
    mutationFn: async () => {
      if (!ticketId) throw new Error("no ticket");
      const { data: u } = await supabaseV2.auth.getUser();
      if (!u.user) throw new Error(t("modal.needLogin"));
      const { error } = await supabaseV2.from("support_messages").insert({
        ticket_id: ticketId,
        author_user_id: u.user.id,
        is_admin: false,
        body: reply.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setReply("");
      queryClient.invalidateQueries({ queryKey: ["support-messages", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets-mine"] });
    },
  });

  const ticket = ticketQuery.data;
  const messages = messagesQuery.data || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        {ticket && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LifeBuoy className="h-5 w-5 text-primary" />
                {ticket.title}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={ticket.status} />
                <span className="text-xs">
                  {t("thread.openedOn", {
                    date: new Date(ticket.created_at).toLocaleString(currentIntlLocale(), {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    }),
                  })}
                </span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
            </div>

            {ticket.status === "resolved" && (
              <p className="text-xs text-muted-foreground italic">{t("thread.closed")}</p>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); if (reply.trim()) sendReply.mutate(); }}
              className="flex gap-2"
            >
              <Input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={t("thread.yourMessagePh")}
                maxLength={5000}
              />
              <Button type="submit" disabled={!reply.trim() || sendReply.isPending}>
                {sendReply.isPending ? t("thread.sending") : t("thread.send")}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const { t } = useTranslation("support");
  const when = new Date(message.created_at).toLocaleString(currentIntlLocale(), {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className={cn("rounded-lg p-3 text-sm border",
      message.is_admin ? "bg-violet-50 border-violet-200" : "bg-card border-border"
    )}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold text-muted-foreground">
          {message.is_admin ? t("thread.adminLabel") : t("thread.youLabel")}
        </span>
        <span className="text-xs text-muted-foreground">{when}</span>
      </div>
      <p className="whitespace-pre-wrap break-words">{message.body}</p>
    </div>
  );
}
